// /signin — DESIGN.md §8.1.
//
// Full-viewport centered hero card (rounded.xl, elevation.2, 480px wide).
// One primary button does a top-level navigation to GET /api/session/login,
// which 302s to /oidc/authorize and then to Federate. We intentionally do
// NOT fetch() this endpoint — the OIDC flow requires the browser's URL
// bar, cookie jar, and top-level navigation context (LLD §4.1). A fetch()
// here transparently follows the 302 as an XHR and CORS-strips the final
// Federate redirect; it also doesn't participate in the first-party cookie
// round-trip needed for `state` / `nonce` cookies (if any).
//
// Error slot:
//   - `?error=<code>` is rendered verbatim in a <StatusChip tone="danger">
//     plus a one-line operator sentence (see ERROR_MESSAGES below).
//   - `?reason=<code>` is rendered the same way; the api client uses this
//     to surface its own 401 redirects.

import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, StatusChip } from "@/components";
import "./SignIn.css";

// POC: single tenant. Matches `id: amazon-wwos` in
// configuration/tenants/{local,beta}.yaml. When we go multi-tenant,
// surface tenant_id via /api/config and read it from useAppConfig().
const TENANT_ID = "amazon-wwos";

const ERROR_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  invalid_nonce:
    "The sign-in callback nonce didn't match. Start the flow again.",
  jwks_unavailable:
    "Could not fetch identity provider signing keys. Try again in a moment.",
  provisioning_pending:
    "Your identity isn't yet mapped. Provisioning takes 1–2 minutes.",
  session_expired: "Your session expired. Sign in again to continue.",
  session_missing: "No active session. Sign in to continue.",
  role_required: "Select a role before continuing.",
  unauthorized: "Sign-in required.",
});

function errorSentence(code: string): string {
  return ERROR_MESSAGES[code] ?? "Sign-in failed.";
}

export function SignInPage(): JSX.Element {
  const location = useLocation();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const urlErrors = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const errs: string[] = [];
    const err = params.get("error");
    const reason = params.get("reason");
    if (err !== null && err !== "") errs.push(err);
    if (reason !== null && reason !== "") errs.push(reason);
    return errs;
  }, [location.search]);

  const handleSignIn = (): void => {
    // Preserve a return_to so the post-callback redirect lands back where
    // the operator intended. `?return_to=` on /signin wins; otherwise go
    // to /dashboard.
    //
    // IMPORTANT: return_to must be an ABSOLUTE URL pointing at the UI
    // origin, not a relative path. /oidc/callback lives on the backend
    // (localhost:8080 in dev), while the SPA lives on the UI origin
    // (localhost:3000 in dev). A relative path would be resolved against
    // the backend and put the user on port 8080, where Coral's filter
    // chain 400s with role_required on BFF paths. Anchoring to
    // window.location.origin keeps the redirect on the UI.
    const params = new URLSearchParams(location.search);
    const rawReturnTo = params.get("return_to");
    let returnTo: string;
    if (rawReturnTo !== null && rawReturnTo !== "") {
      returnTo = /^https?:\/\//.test(rawReturnTo)
        ? rawReturnTo
        : `${window.location.origin}${
            rawReturnTo.startsWith("/") ? "" : "/"
          }${rawReturnTo}`;
    } else {
      returnTo = `${window.location.origin}/dashboard`;
    }

    setSubmitting(true);
    const qs = new URLSearchParams({
      tenant_id: TENANT_ID,
      return_to: returnTo,
    }).toString();
    // Top-level navigation — NOT fetch(). The browser must own the redirect
    // chain all the way to Federate.
    window.location.assign(`/api/session/login?${qs}`);
  };

  return (
    <div className="signin-hero">
      <div className="signin-card">
        <div className="signin-card__brand">Ring</div>
        <div className="signin-card__tagline">Business</div>

        <div className="signin-card__action">
          <Button
            variant="primary"
            size="lg"
            onClick={handleSignIn}
            loading={submitting}
          >
            Sign in with Amazon Federate
          </Button>
          <div className="signin-card__caption">
            You will be redirected to Midway.
          </div>
        </div>

        {urlErrors.length > 0 && (
          <div className="signin-card__errors" role="alert">
            {urlErrors.map((code) => (
              <div key={`url-${code}`} className="signin-card__error">
                <StatusChip tone="danger">
                  <code className="mono mono--sm">{code}</code>
                </StatusChip>
                <span className="signin-card__error-sentence">
                  {errorSentence(code)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
